import { useCallback, useEffect, useState } from 'react';
import { useApi } from '../useApi';
import { roundedForServer, NEARBY_SHOWN } from '../utils/courseDistance';

/**
 * Directory courses near `anchor` that the society hasn't got yet — the
 * "In the directory" rows under NEAR YOU (docs/nearby-courses-design.md §2.7).
 *
 * Asked only when `enabled` (a course manager, with an anchor, not searching):
 * the endpoint is MANAGE_COURSES, and a member would be shown a row whose
 * only button they may not press. The position goes rounded to ~1 km.
 *
 * -> { rows, remove(id) }. A failed lookup is simply no rows: this group is a
 * suggestion, and a picker that errors over a suggestion is worse than one
 * without it.
 */
export function useDirectoryNearby({ anchor, enabled }) {
  const { authenticatedFetch } = useApi();
  const [rows, setRows] = useState([]);
  const point = enabled && anchor ? roundedForServer(anchor) : null;
  const key = point ? `${point.lat},${point.lng}` : null;

  useEffect(() => {
    if (!key) { setRows([]); return undefined; }
    let live = true;
    const [lat, lng] = key.split(',');
    (async () => {
      try {
        const res = await authenticatedFetch(
          `/global-courses/nearby?lat=${lat}&lng=${lng}&limit=${NEARBY_SHOWN}`);
        const body = res.ok ? await res.json() : [];
        if (live) setRows(Array.isArray(body) ? body : []);
      } catch {
        if (live) setRows([]);
      }
    })();
    return () => { live = false; };
  }, [key, authenticatedFetch]);

  const remove = useCallback((id) => setRows((prev) => prev.filter((r) => r.id !== id)), []);
  return { rows, remove };
}
